import { useMemo } from "react";
import { AxiosError } from "axios";
import { useMutation } from "react-query";

import activities from "../../../redux/activities";
import { BasicFarm } from "../../../models/farms";
import { MutationEnvelope, ensureMutationState } from "../../../services/query";
import { getAuthToken } from "../../../services/granularAxios";
import farmsService from "../../../services/farms";
import { ensureValue } from "../../../services/util";

import { useMaybeUserId } from "../userId";
import { useMaybeCurrentOperationId } from "../operations";

import { useInvalidateFarms } from "./farms.invalidate";
import { useSetFarmsGlobalActivityAndStatus } from "./farms.globalStatus";
import { useMaybeFabricFeatureFlag } from "../featureFlags";

// ###################
// ## useDeleteFarm ##
// ###################

export type ResultOfUseDeleteFarm = MutationEnvelope<string, null>;
export type UseDeleteFarm = () => ResultOfUseDeleteFarm;

/**
 * Use the `mutate` function returned from this hook to delete the farm.
 * This will trigger an invalidation of the farms data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useDeleteFarm: UseDeleteFarm = () => {
  const invalidateFarms = useInvalidateFarms();
  const maybeUserId = useMaybeUserId();
  const maybeOperationId = useMaybeCurrentOperationId();
  const catalogFlag = Boolean(
    useMaybeFabricFeatureFlag({
      key: "fields-api",
    }),
  );

  const setFarmsGlobalActivityAndStatus = useSetFarmsGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    null,
    AxiosError,
    string
  >(
    (payload) => {
      const userId = ensureValue(maybeUserId, "userId");
      const operationId = ensureValue(maybeOperationId, "operationId");

      return farmsService.removeFarm(
        {
          authToken: getAuthToken(),
          userId,
          operationId,
          farmId: payload,
        },
        catalogFlag,
      );
    },
    {
      onMutate: () => {
        setFarmsGlobalActivityAndStatus({
          activity: activities.removeFarm,
          status: "loading",
        });
      },
      onSuccess: async () => {
        await invalidateFarms();

        setFarmsGlobalActivityAndStatus({
          activity: activities.removeFarm,
          status: "success",
        });
      },
      onError: () => {
        setFarmsGlobalActivityAndStatus({
          activity: activities.removeFarm,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};

// ###################
// ## useCreateFarm ##
// ###################

export type NewFarm = { name: string };
export type ResultOfUseCreateFarm = MutationEnvelope<NewFarm, BasicFarm>;
export type UseCreateFarm = () => ResultOfUseCreateFarm;

/**
 * Use the `mutate` function returned from this hook to create a new farm.
 * This will trigger an invalidation of the farms data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useCreateFarm: UseCreateFarm = () => {
  const invalidateFarms = useInvalidateFarms();
  const maybeUserId = useMaybeUserId();
  const maybeOperationId = useMaybeCurrentOperationId();
  const catalogFlag = Boolean(
    useMaybeFabricFeatureFlag({
      key: "fields-api",
    }),
  );

  const setFarmsGlobalActivityAndStatus = useSetFarmsGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    BasicFarm,
    AxiosError,
    NewFarm
  >(
    (payload) => {
      const userId = ensureValue(maybeUserId, "userId");
      const operationId = ensureValue(maybeOperationId, "operationId");

      return farmsService.addFarm(
        {
          authToken: getAuthToken(),
          userId,
          operationId,
          farmName: payload.name,
        },
        catalogFlag,
      );
    },
    {
      onMutate: () => {
        setFarmsGlobalActivityAndStatus({
          activity: activities.addFarm,
          status: "loading",
        });
      },
      onSuccess: async () => {
        await invalidateFarms();

        setFarmsGlobalActivityAndStatus({
          activity: activities.addFarm,
          status: "success",
        });
      },
      onError: () => {
        setFarmsGlobalActivityAndStatus({
          activity: activities.addFarm,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};

// ###################
// ## useUpdateFarm ##
// ###################

export type ResultOfUseUpdateFarm = MutationEnvelope<BasicFarm, BasicFarm>;
export type UseUpdateFarm = () => ResultOfUseUpdateFarm;

/**
 * Use the `mutate` function returned from this hook to update an existing farm.
 * This will trigger an invalidation of the farms data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useUpdateFarm: UseUpdateFarm = () => {
  const invalidateFarms = useInvalidateFarms();
  const maybeUserId = useMaybeUserId();
  const maybeOperationId = useMaybeCurrentOperationId();
  const catalogFlag = Boolean(
    useMaybeFabricFeatureFlag({
      key: "fields-api",
    }),
  );

  const setFarmsGlobalActivityAndStatus = useSetFarmsGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    BasicFarm,
    AxiosError,
    BasicFarm
  >(
    (payload) => {
      const userId = ensureValue(maybeUserId, "userId");
      const operationId = ensureValue(maybeOperationId, "operationId");

      return farmsService.updateFarm(
        {
          authToken: getAuthToken(),
          userId,
          operationId,
          farm: payload,
        },
        catalogFlag,
      );
    },
    {
      onMutate: () => {
        setFarmsGlobalActivityAndStatus({
          activity: activities.renameFarm,
          status: "loading",
        });
      },
      onSuccess: async () => {
        await invalidateFarms();

        setFarmsGlobalActivityAndStatus({
          activity: activities.renameFarm,
          status: "success",
        });
      },
      onError: () => {
        setFarmsGlobalActivityAndStatus({
          activity: activities.renameFarm,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};
